import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InvestorLayout from '../components/InvestorLayout';
import investorService from '../services/investorService';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { Upload, Download, Trash2, FileText, AlertCircle, CheckCircle, Info, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FORMATS = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

const InvestorDocumentsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();

  const [documents, setDocuments] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [documentType, setDocumentType] = useState('');
  const [investmentId, setInvestmentId] = useState('none');
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [fileError, setFileError] = useState('');

  // Belge tipleri
  const documentTypes = [
    'passport',
    'residence_permit',
    'purchase_contract',
    'tapu',
    'tax_certificate',
    'military_permit',
    'power_of_attorney',
    'bank_statement',
    'other',
  ];

  // Durum badge renkleri
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      verified: 'bg-green-100 text-green-700 hover:bg-green-200',
      rejected: 'bg-red-100 text-red-700 hover:bg-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Durum ikonu
  const getStatusIcon = (status) => {
    if (status === 'verified') return CheckCircle;
    if (status === 'rejected') return AlertCircle;
    return Info;
  };

  const fetchData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [documentsResult, investmentsResult] = await Promise.all([
        investorService.getDocuments(user.id),
        investorService.getInvestments(user.id),
      ]);

      if (documentsResult.error) throw documentsResult.error;
      if (investmentsResult.error) throw investmentsResult.error;

      setDocuments(documentsResult.data || []);
      setInvestments(investmentsResult.data || []);

    } catch (err) {
      console.error('Documents fetch error:', err);
      setError(err.message || t('investor.dashboard.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Dosya seçimi
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setFileError('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Dosya boyutu kontrolü
    if (file.size > MAX_FILE_SIZE) {
      setFileError(t('documents.maxFileSize'));
      setSelectedFile(null);
      return;
    }

    // Format kontrolü
    if (!ACCEPTED_FORMATS.includes(file.type)) {
      setFileError(t('documents.acceptedFormats'));
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  // Belge yükleme
  const handleUpload = async () => {
    if (!documentType || !selectedFile) {
      toast({
        title: t('auth.required_field'),
        description: t('documents.uploadError'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);

      const { data, error } = await investorService.uploadDocument({
        user_id: user.id,
        investment_id: investmentId !== 'none' ? investmentId : null,
        document_type: documentType,
        file: selectedFile,
        description,
      });

      if (error) throw error;

      toast({
        title: t('documents.uploadSuccess'),
        variant: 'default',
      });

      // Formu temizle
      setDocumentType('');
      setInvestmentId('none');
      setSelectedFile(null);
      setDescription('');
      setFileError('');

      // Listeyi yenile
      await fetchData();

    } catch (err) {
      console.error('Upload error:', err);
      toast({
        title: t('documents.uploadError'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  // Belge silme
  const handleDelete = async (documentId) => {
    if (!window.confirm(t('documents.deleteConfirm'))) return;

    try {
      const { error } = await investorService.deleteDocument(documentId);
      if (error) throw error;

      toast({
        title: t('common.view'),
        description: 'Belge başarıyla silindi',
      });

      await fetchData();
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Hata',
        description: err.message,
        variant: 'destructive',
      });
    }
  };

  // Skeleton bileşeni
  const DocumentCardSkeleton = () => (
    <div className="p-4 border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-5 w-20" />
      </div>
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-24" />
    </div>
  );

  if (error) {
    return (
      <InvestorLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle size={48} className="text-red-500 mb-4" />
          <h2 className="text-2xl font-heading text-[#0A192F] mb-2">
            {t('investor.dashboard.error_title')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={fetchData}
            className="bg-[#0A192F] text-white hover:bg-[#112240]"
          >
            <RefreshCw size={20} className="mr-2" />
            {t('investor.dashboard.retry')}
          </Button>
        </div>
      </InvestorLayout>
    );
  }

  return (
    <InvestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading text-[#0A192F] mb-2">
            {t('documents.title')}
          </h1>
          <p className="text-gray-600">
            {t('investor.dashboard.welcome')}
          </p>
        </div>

        {/* Gerekli Belgeler Bilgilendirme */}
        <Card className="border border-blue-200 bg-blue-50">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Info size={20} className="text-blue-600" />
              <CardTitle className="text-lg font-heading text-blue-900">
                {t('documents.requiredDocuments')}
              </CardTitle>
            </div>
            <CardDescription className="text-blue-800">
              {t('documents.requiredDocumentsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-blue-900">
              {documentTypes.map((type) => (
                <li key={type} className="flex items-start space-x-2">
                  <CheckCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>{t(`documents.documentInfo.${type}`).split(' (')[0]}:</strong>{' '}
                    {t(`documents.documentInfo.${type}`)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Belge Yükleme Formu */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-[#0A192F]">
              {t('documents.uploadDocument')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Belge Tipi */}
            <div className="space-y-2">
              <Label htmlFor="documentType">{t('documents.documentType')}</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger id="documentType">
                  <SelectValue placeholder={t('documents.documentType')} />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`documents.documentInfo.${type}`).split(' (')[0]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* İlgili Yatırım */}
            <div className="space-y-2">
              <Label htmlFor="investmentId">{t('documents.relatedInvestment')}</Label>
              <Select value={investmentId} onValueChange={setInvestmentId}>
                <SelectTrigger id="investmentId">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('documents.noInvestment')}</SelectItem>
                  {investments.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.property?.title || `Investment #${inv.id.slice(0, 8)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dosya Seçimi */}
            <div className="space-y-2">
              <Label htmlFor="file">{t('documents.selectFile')}</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="cursor-pointer"
              />
              <div className="flex flex-col space-y-1 text-xs text-gray-500">
                <span>{t('documents.maxFileSize')}</span>
                <span>{t('documents.acceptedFormats')}</span>
              </div>
              {fileError && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle size={16} />
                  <AlertDescription className="text-sm">{fileError}</AlertDescription>
                </Alert>
              )}
              {selectedFile && !fileError && (
                <Alert className="py-2 bg-green-50 border-green-200">
                  <CheckCircle size={16} className="text-green-600" />
                  <AlertDescription className="text-sm text-green-800">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('documents.description')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('documents.description')}
                rows={3}
              />
            </div>

            {/* Yükle Butonu */}
            <Button
              onClick={handleUpload}
              disabled={!documentType || !selectedFile || uploading || !!fileError}
              className="w-full bg-[#D4AF37] text-white hover:bg-[#B5952F] disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Yükleniyor...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  {t('documents.upload')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Yüklenen Belgeler */}
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-[#0A192F]">
              {t('documents.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DocumentCardSkeleton />
                <DocumentCardSkeleton />
                <DocumentCardSkeleton />
              </div>
            ) : documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documents.map((doc) => {
                  const StatusIcon = getStatusIcon(doc.status);
                  return (
                    <div
                      key={doc.id}
                      className="p-4 border border-gray-200 rounded-lg hover:border-[#D4AF37] transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <FileText size={20} className="text-[#0A192F]" />
                          <h4 className="font-semibold text-[#0A192F]">
                            {t(`documents.documentInfo.${doc.document_type}`).split(' (')[0]}
                          </h4>
                        </div>
                        <Badge className={getStatusColor(doc.status)}>
                          <StatusIcon size={14} className="mr-1" />
                          {doc.status === 'verified'
                            ? t('investor.document_status.approved')
                            : doc.status === 'rejected'
                              ? t('investor.document_status.rejected')
                              : t('investor.document_status.pending')}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2 truncate">
                        {doc.file_name}
                      </p>
                      
                      <p className="text-xs text-gray-500 mb-3">
                        {new Date(doc.uploaded_at).toLocaleDateString('tr-TR')}
                      </p>

                      {doc.description && (
                        <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                          {doc.description}
                        </p>
                      )}

                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
                          onClick={() => window.open(doc.file_url, '_blank')}
                        >
                          <Download size={14} className="mr-1" />
                          {t('documents.view')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <FileText size={56} className="text-gray-300 mb-4" />
                <h3 className="text-xl font-heading text-[#0A192F] mb-2">
                  {t('documents.noDocuments')}
                </h3>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </InvestorLayout>
  );
};

export default InvestorDocumentsPage;
